import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { eventChannel, buffers } from 'redux-saga';
import { select, call, takeEvery, all, fork, take } from 'redux-saga/effects';
import isSSR from '../../utils/isSSR';
import biLogger from '@wix/da-bi/pkg/BiLogger.logger';
import { PureBiData } from '@wix/da-bi/pkg/events';
import { logPageView } from './actions';
import { getPublicSession } from '../../publicSession/selectors';
import { getBiContextData } from './selectors';
import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import ddt from '@wix/da-ddt';
// helper for logging events from sagas
export function* logBiEvent(data, isPageView = false) {
    const contextData = yield select(isPageView ? getPublicSession : getBiContextData);
    yield call([
        biLogger,
        'log'
    ], contextData, data);
}
function* handleLogPageView({ payload }) {
    if (isSSR()) {
        return;
    }
    // TODO make these not use a reflow
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    switch(payload.devpageLoadingPhase){
        // Phase 2 is when we have lazy loaded related content (in the sidebar / BTF) this will not
        // be fired all the time as lazyloading is conditional
        case 'phase2':
            yield call(logBiEvent, PureBiData(_object_spread_props(_object_spread({
                evid: 8,
                screen_width: screenWidth,
                screen_height: screenHeight
            }, payload), {
                metadata: payload.metadata
            })), true);
            break;
        // Phase 1.5 is the page fully loaded, this means all the extended data has loaded
        case 'phase1-5':
            yield call(logBiEvent, PureBiData(_object_spread_props(_object_spread({
                evid: 9,
                screen_width: screenWidth,
                screen_height: screenHeight
            }, payload), {
                metadata: payload.metadata
            })), true);
            break;
        // Phase 1 is the standard pageview, for devpage this means page requested when duperbrowsing
        case 'phase1':
        default:
            yield call(logBiEvent, PureBiData(_object_spread_props(_object_spread({
                evid: 99,
                screen_width: screenWidth,
                screen_height: screenHeight
            }, payload), {
                metadata: payload.metadata
            })), true);
    }
}
function makeBiData(beat, metrics) {
    var _window___APP_INFO__;
    const data = {
        evid: 1,
        // project
        appinf: (_window___APP_INFO__ = window['__APP_INFO__']) !== null && _window___APP_INFO__ !== void 0 ? _window___APP_INFO__ : 'unknown',
        beat
    };
    const metricMap = {
        FP: 'p_fp',
        FCP: 'p_fcp',
        FID: 'p_fid',
        INP: 'p_inp',
        LCP: 'p_lcp',
        CLS: 'p_cls',
        TTFB: 'nt_ttfb',
        fetchTime: 'nt_ft',
        workerTime: 'nt_wt',
        totalTime: 'nt_tt',
        downloadTime: 'nt_dt',
        headerSize: 'nt_hs',
        dnsLookupTime: 'nt_dlt',
        downlink: 'ni_dl',
        effectiveType: 'ni_et',
        rtt: 'ni_rtt',
        saveData: 'ni_sd'
    };
    for (const metric of metrics){
        // Record the vital bracket poor, needs improvement, good
        if (metric.vitalsScore) {
            data[`${metric.type}_vs`.toLocaleLowerCase()] = metric.vitalsScore;
        }
        // Add debug if present
        if (metric.debugInfo) {
            data.debug_info = metric.debugInfo;
        }
        if (metric.duration && metricMap[metric.type]) {
            // Record things with a duration only
            data[metricMap[metric.type]] = Math.round(metric.duration);
            continue;
        }
        // Walk the data object and extract stuff
        if (metric.data) {
            const keys = Object.keys(metric.data);
            if (keys.length > 0) for (const key of keys){
                if (!metricMap[key]) {
                    continue; // skip missing
                }
                if (isFinite(metric.data[key])) {
                    data[metricMap[key]] = Math.round(metric.data[key]);
                } else {
                    data[metricMap[key]] = metric.data[key];
                }
            }
        }
    }
    // Track server timings
    if (beat === 'fcp') {
        const [navEntry] = performance.getEntriesByType('navigation');
        if (navEntry && navEntry.serverTiming && navEntry.serverTiming.length) {
            navEntry.serverTiming.forEach((st)=>{
                if (st.name === 'r') {
                    data['st_react'] = Math.round(st.duration);
                }
                if (st.name === 'p') {
                    data['st_papi'] = Math.round(st.duration);
                }
                if (st.name === 'o') {
                    data['st_over'] = Math.round(st.duration);
                }
                if (st.name === 't') {
                    data['st_total'] = Math.round(st.duration);
                }
            });
        }
    }
    return PureBiData(data);
}
function* handlePerfLogging() {
    if (typeof window === 'undefined') {
        return;
    }
    let Perfume;
    try {
        Perfume = yield import('perfume.js');
    } catch (e) {
        return;
    }
    const perfumeChannel = eventChannel((emitter)=>{
        Perfume.initPerfume({
            resourceTiming: false,
            analyticsTracker: (log)=>emitter(log)
        });
        return ()=>{};
    }, buffers.expanding());
    let metrics = [];
    let fcpDone = false, ttfbDone = false;
    while(true){
        const log = yield take(perfumeChannel);
        const { metricName, data, rating: vitalsScore, attribution } = log;
        ddt.log('perfume', 'PerfData', {
            metricName,
            data,
            vitalsScore,
            attribution
        });
        let duration;
        if (isNumber(data)) {
            duration = data;
        }
        switch(metricName){
            case 'navigationTiming':
                metrics.push({
                    type: metricName,
                    data,
                    vitalsScore
                });
                break;
            case 'networkInformation':
                metrics.push({
                    type: metricName,
                    data
                });
                break;
            case 'FCP':
                fcpDone = true;
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore,
                    debugInfo: getDebugFromAttribution(metricName, attribution)
                });
                if (ttfbDone) {
                    yield call(logBiEvent, makeBiData('fcp', metrics));
                }
                break;
            case 'TTFB':
                ttfbDone = true;
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore,
                    debugInfo: getDebugFromAttribution(metricName, attribution)
                });
                if (fcpDone) {
                    yield call(logBiEvent, makeBiData('fcp', metrics));
                }
                break;
            case 'INP':
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore,
                    debugInfo: getDebugFromAttribution(metricName, attribution, vitalsScore)
                });
                yield call(logBiEvent, makeBiData('inp', metrics));
                metrics = [];
                break;
            default:
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore,
                    debugInfo: getDebugFromAttribution(metricName, attribution)
                });
                if (metricName === 'LCP') {
                    yield call(logBiEvent, makeBiData('lcp', metrics));
                    metrics = [];
                }
                break;
        }
    }
}
// https://github.com/GoogleChrome/web-vitals#metricwithattribution
function getDebugFromAttribution(metricName, attrib, score) {
    const { largestShiftTarget, loadState, eventTarget, eventType, timeToFirstByte, firstByteToFCP, waitingTime, dnsTime, connectionTime, requestTime, element, url, elementRenderDelay, resourceLoadTime, resourceLoadDelay } = attrib;
    try {
        switch(metricName){
            case 'CLS':
                return JSON.stringify({
                    largestShiftTarget,
                    loadState
                });
            case 'FID':
            case 'INP':
                // TODO log to BI, want to see volume first
                if (metricName === 'INP' && score === 'poor' && eventTarget !== '' && Math.floor(Math.random() * 1000) === 0) {
                    var _window___APP_INFO__;
                    const appinfo = (_window___APP_INFO__ = window['__APP_INFO__']) !== null && _window___APP_INFO__ !== void 0 ? _window___APP_INFO__ : 'unknown';
                    const app = appinfo.split(':');
                    ddt.alert('perf', `Got poor INP (${app[0]})`, [
                        'perf',
                        appinfo
                    ], {
                        eventTarget,
                        eventType,
                        loadState
                    });
                }
                return JSON.stringify({
                    eventTarget,
                    eventType,
                    loadState
                });
            case 'LCP':
                return JSON.stringify({
                    element,
                    url,
                    elementRenderDelay,
                    resourceLoadTime,
                    resourceLoadDelay
                });
            case 'FCP':
                return JSON.stringify({
                    timeToFirstByte,
                    firstByteToFCP
                });
            case 'TTFB':
                return JSON.stringify({
                    waitingTime,
                    dnsTime,
                    connectionTime,
                    requestTime
                });
            default:
                return undefined;
        }
    } catch (e) {
        return undefined;
    }
}
export default function* rootSaga() {
    yield fork(handlePerfLogging);
    yield all([
        takeEvery(logPageView, handleLogPageView)
    ]);
}

//# sourceMappingURL=saga.js.map